import { defineRecipe } from '@chakra-ui/react';

const disabledBaseStyles = {
  color: 'on-navy.200',
  borderColor: 'on-navy.200',
  opacity: 1,

  _hover: {
    boxShadow: 'none',
  },
};

export const button = defineRecipe({
  className: 'chakra-button',
  base: {
    borderRadius: 'full',
    fontWeight: 'semibold',
  },

  variants: {
    variant: {
      outline: {
        borderWidth: '1px',
        borderColor: 'colorPalette.solid',
        color: 'colorPalette.solid',

        _disabled: disabledBaseStyles,
        _hover: {
          // override the default hover background
          bg: 'transparent',
          boxShadow: 'var(--chakra-shadows-md)',
        },
        _active: {
          bg: 'colorPalette.50',
          boxShadow: 'none',
        },
        _expanded: {
          bg: 'colorPalette.50',
        },
        _dark: {
          borderColor: 'link.muted',
          color: 'fg.muted',

          _disabled: {
            borderColor: 'border',
            color: 'gray.600',
            opacity: 0.5,
          },
          _hover: {
            // override the default hover background
            bg: 'transparent',
            boxShadow: 'var(--chakra-shadows-md)',
          },
          _active: {
            borderColor: 'link.muted',
            color: 'fg.muted',
            bg: 'none',
            boxShadow: 'none',
          },
          _expanded: {
            bg: 'none',
          },
        },
      },
      solid: {
        _disabled: {
          ...disabledBaseStyles,
          bg: 'on-navy.50',
        },
        _hover: {
          bg: 'colorPalette.solid',
          shadow: 'var(--chakra-shadows-md)',

          // this overrides the default Chakra rule for disabled hover button styles
          _disabled: {
            bg: 'on-navy.50',
          },
        },
        _active: {
          bg: 'colorPalette.fg',
          boxShadow: 'none',
        },
        _dark: {
          bg: 'link',
          color: 'white',
          _disabled: {
            bg: 'link.subtle',
            borderColor: 'link.subtle',
          },
          _hover: {
            bg: 'link',
            shadow: 'var(--chakra-shadows-md)',

            // this overrides the default Chakra rule for disabled hover button styles
            _disabled: {
              bg: 'link',
            },
          },
          _active: {
            bg: 'link',
            boxShadow: 'none',
          },
        },
      },
      ghost: {
        border: 'none',
        _disabled: {
          color: 'on-navy.200',
          opacity: 0.4,

          _hover: {
            color: 'on-navy.200',
            textDecoration: 'none',
          },
        },
        color: 'colorPalette.solid',
        _hover: {
          // override the default hover background
          bg: 'transparent',
          color: 'colorPalette.fg',
          textDecoration: 'underline',
        },

        _expanded: {
          bg: 'colorPalette.subtle',
        },
        _dark: {
          border: 'none',
          _disabled: {
            color: 'link.subtle',

            _hover: {
              color: 'link.subtle',
              textDecoration: 'none',
            },
          },
          color: 'link',
          _hover: {
            // override the default hover background
            bg: 'transparent',
            color: 'link',
            textDecoration: 'underline',
          },

          _expanded: {
            bg: 'link',
          },
        },
      },

      link: {
        color: 'colorPalette.solid',
        _hover: {
          bg: 'none',
          textDecoration: 'underline',
          color: 'colorPalette.fg',
        },
        _active: {
          bg: 'none',
          color: 'colorPalette.900',
          textDecoration: 'underline',
        },

        _expanded: {
          bg: 'colorPalette.subtle',
        },
        _dark: {
          color: 'link',
          _hover: {
            bg: 'none',
            textDecoration: 'underline',
            color: 'link',
          },
          _active: {
            bg: 'none',
            color: 'link',
            textDecoration: 'underline',
          },
          _expanded: {
            bg: 'none',
          },
        },
      },

      select: {
        borderRadius: 'md',
        border: '1px solid',
        fontWeight: 'normal',
        _hover: {
          borderColor: 'blue.300',
        },
        _focusVisible: {
          borderColor: 'blue.500',
          boxShadow: `0 0 0 1px var(--chakra-colors-blue-focus-ring)`, // blue.50
        },
        _disabled: {
          bg: 'gray.100',
          borderColor: 'on-navy.200',
          opacity: 0.4,
          _hover: {
            // Disable hover state for disabled
            borderColor: 'on-navy.200',
          },
        },
        _dark: {
          borderRadius: 'sm',
          border: '1px solid',
          fontWeight: 'normal',
          _hover: {
            borderColor: 'blue.300',
          },
          _focusVisible: {
            borderColor: 'link',
            boxShadow: `0 0 0 1px var(--chakra-colors-blue-focus-ring)`, // blue.50
          },
          _disabled: {
            bg: 'gray.100',
            borderColor: 'on-navy.200',
            opacity: 0.4,
            _hover: {
              // Disable hover state for disabled
              borderColor: 'on-navy.200',
            },
          },
        },
      },
    },
    size: {
      md: {
        fontSize: 'md',
        _icon: {
          width: '3.5',
          height: '3.5',
        },
      },
      sm: {
        paddingInline: 3,
        minWidth: 8,
        height: 8,
        _icon: {
          width: '3',
          height: '3',
        },
      },
    },
  },

  defaultVariants: {
    variant: 'outline',
  },
});
